import { Stack } from '@mui/material';
import { ComponentType, useState } from 'react';

import { Button } from '@/components/form/baseInputs/Button';
import { FullScreenModal } from '@/components/modals/FullScreenModal/FullScreenModal';
import { Footer } from '@/components/navigation/Footer';
import { GratDesignerStages } from '@/navigation/constants';
import { CONFIRMATION_MESSAGE } from '@/navigation/UnloadPrompt.provider';
// eslint-disable-next-line luminary-rules/no-page-imports
import { GRATDesigner } from '@/pages/designer/GRATDesigner/GRATDesigner';
// eslint-disable-next-line luminary-rules/no-page-imports
import {
  TAB_NAMES,
  TabName,
} from '@/pages/designer/GRATDesigner/StructuringDesigner/constants';
import { COLORS } from '@/styles/tokens/colors';

import { NEW_ENTITY_SENTINEL } from '../../entities.constants';
import { DesignerStagePicker } from './DesignerStagePicker';

interface GRATDesignerTrowserProps {
  isOpen?: boolean;
  householdId: string;
  entityId?: string;
  onFormAction: (
    action?: 'cancel' | 'basicInformation' | 'structuring',
    entityId?: string
  ) => void;
  initialStage: GratDesignerStages;
  initialStructuringTab?: TabName;
  Footer?: ComponentType<GRATDesignerModalFooterProps>;
}

export function GRATDesignerTrowser({
  isOpen,
  householdId,
  entityId = NEW_ENTITY_SENTINEL,
  initialStage,
  initialStructuringTab,
  onFormAction,
  Footer,
}: GRATDesignerTrowserProps) {
  const [editingEntityId, setEditingEntityId] = useState(entityId);
  const [isDirty, setIsDirty] = useState(false);
  const [currentStage, setCurrentStage] =
    useState<GratDesignerStages>(initialStage);

  const shouldBlock = isDirty && isOpen;

  const handleFormUpdated = (
    stage: 'basicInformation' | 'structuring',
    entityId: string
  ) => {
    onFormAction?.(stage, entityId);

    if (stage === 'basicInformation') {
      setCurrentStage(GratDesignerStages.STRUCTURING);
    }
    setEditingEntityId(entityId);
  };

  const handleCloseModal = () => {
    if (shouldBlock) {
      const isConfirmed = window.confirm?.(CONFIRMATION_MESSAGE);

      if (!isConfirmed) {
        return;
      }
    }

    onFormAction?.('cancel');
  };

  const handleChangeStage = (newStage: GratDesignerStages) => {
    if (shouldBlock) {
      const isConfirmed = window.confirm?.(CONFIRMATION_MESSAGE);

      if (!isConfirmed) {
        return;
      }
    }

    setCurrentStage(newStage);
  };

  return (
    <FullScreenModal isOpen={isOpen} backdropSx={{ bgcolor: COLORS.GRAY[50] }}>
      {isOpen && (
        <GRATDesigner
          householdId={householdId}
          entityId={editingEntityId}
          designerStage={
            // Guarantee that the editingEntityId updates before rendering the designer
            currentStage === GratDesignerStages.STRUCTURING &&
            editingEntityId === NEW_ENTITY_SENTINEL
              ? GratDesignerStages.BASIC_INFORMATION
              : currentStage
          }
          initialStructuringTab={initialStructuringTab || TAB_NAMES.ASSETS}
          isFullScreenModal={true}
          onAfterUpdate={handleFormUpdated}
          onDirtyChange={setIsDirty}
          DesignerFooter={({ disabled }) => {
            if (Footer) {
              return (
                <Footer
                  disabled={disabled}
                  currentStage={currentStage}
                  onCloseModal={handleCloseModal}
                  onChangeStage={handleChangeStage}
                />
              );
            }

            return (
              <GRATDesignerModalFooter
                disabled={disabled}
                currentStage={currentStage}
                onCloseModal={handleCloseModal}
                onChangeStage={handleChangeStage}
              />
            );
          }}
        />
      )}
    </FullScreenModal>
  );
}

export interface GRATDesignerModalFooterProps {
  disabled?: boolean;
  currentStage: GratDesignerStages;
  onCloseModal: () => void;
  onChangeStage: (newStage: GratDesignerStages) => void;
}

export function GRATDesignerModalFooter({
  disabled,
  currentStage,
  onCloseModal,
  onChangeStage,
}: GRATDesignerModalFooterProps) {
  return (
    <Footer
      leftAction={
        <Button
          disabled={disabled}
          onClick={onCloseModal}
          variant="secondary"
          size="sm"
        >
          Exit without saving
        </Button>
      }
      rightAction={
        <Stack direction="row" spacing={1}>
          <DesignerStagePicker
            currentStage={currentStage}
            onChangeStage={onChangeStage}
          />
          <Button disabled={disabled} type="submit" variant="primary" size="sm">
            Save and exit
          </Button>
        </Stack>
      }
    />
  );
}
